import { isPlatformBrowser } from '@angular/common';
import {
  computed,
  inject,
  Injectable,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { KioskConfigService } from '@millionshow/api-client';
import { firstValueFrom } from 'rxjs';
import {
  FALLBACK_KIOSK_CONFIG,
  isActiveKioskConfig,
  KIOSK_CONFIG_CACHE_KEY,
  KioskRuntimeConfig,
} from '../config/kiosk-config.model';
import { KioskThemeService } from '../services/kiosk-theme.service';

@Injectable({ providedIn: 'root' })
export class KioskConfigStore {
  private readonly service = inject(KioskConfigService);
  private readonly theme = inject(KioskThemeService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly state = signal<KioskRuntimeConfig>(FALLBACK_KIOSK_CONFIG);
  private initialized = false;

  readonly config = this.state.asReadonly();
  readonly sourceType = computed(() => this.state().sourceType);
  readonly ready = signal(false);

  async initialize(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;
    let selected = FALLBACK_KIOSK_CONFIG;

    if (isPlatformBrowser(this.platformId)) {
      try {
        const response = await firstValueFrom(
          this.service.kioskConfigControllerGetActiveV1()
        );
        if (isActiveKioskConfig(response)) {
          selected = response;
          localStorage.setItem(
            KIOSK_CONFIG_CACHE_KEY,
            JSON.stringify(response)
          );
        } else {
          throw new Error('Invalid kiosk config response');
        }
      } catch {
        try {
          const cached = JSON.parse(
            localStorage.getItem(KIOSK_CONFIG_CACHE_KEY) ?? 'null'
          );
          if (isActiveKioskConfig(cached)) selected = cached;
        } catch {
          localStorage.removeItem(KIOSK_CONFIG_CACHE_KEY);
        }
      }
    }

    this.state.set(selected);
    this.theme.apply(selected.primaryPalette);
    this.ready.set(true);
  }
}
